import { Injectable } from '@angular/core';
import { IDashboardDeviceSummary } from '../types/idashboard-device-summary';
import { deviceTypeIcons } from '../enums/device-type-icons.dict';
import { sensorFields } from '../enums/sensor-fields.dict';
import { DeviceModelFactoryService } from '@caslink/device-client/ng-app/shared/services/device-model-factory.service';
import { DashboardParentTile } from '../types/dashboard-parent-tile';
import { DashboardTile } from '../types/dashboard-tile';
import { IDashboard } from '../types/idashboard';
import { DashboardDeviceTile } from '../types/dashboard-device-tile';
import { IDashboardAreaSummary } from '../types/idashboard-area-summary';
import { ICardDataView } from '../models/device-info.model';
import {
    GraphicBehaviorEnumSensor
} from '@caslink/device-client/ng-app/display-info-icon/graphic-behavior-enum-sensor.interface';
import {
    GraphicBehaviorToggleSensor
} from '@caslink/device-client/ng-app/display-info-icon/graphic-behavior-toggle-sensor.interface';
import { SelectListOption } from '@caslink/device-client/ng-app/display-info-icon/select-list-option.interface';
import { ActionButton } from '../../../common/types/action-button';
import { LightsToggleSensor } from '../types/graphic-behavior/lights-toggle-sensor';
import { RoomLightToggleSensor } from '../types/graphic-behavior/room-light-toggle-sensor';
import { HoodLightToggleSensor } from '../types/graphic-behavior/hood-light-toggle-sensor';
import { FansToggleSensor } from '../types/graphic-behavior/fans-toggle-sensor';
import { RoomFanToggleSensor } from '../types/graphic-behavior/room-fan-toggle-sensor';
import { HoodFanToggleSensor } from '../types/graphic-behavior/hood-fan-toggle-sensor';
import { OccupancyToggleSensor } from '../types/graphic-behavior/occupancy-toggle-sensor';
import { DashboardParentTileEditableProperties } from '../types/dashboard-parent-tile-editable-properties';
import { DashboardViewService } from '@caslink/device-client/ng-app/shared/services/dashboard-view.service';
import { Router } from '@angular/router';
import { DashboardSubscriptionService } from './dashboard-subscription.service';
import { Observable } from 'rxjs';
import { FavoritePlacesService } from 'src/app/modules/favorite/services/favorite-places.service';
import { FavoriteSpacesService } from 'src/app/modules/favorite/services/favorite-spaces.service';
import { FavoriteControlsService } from 'src/app/modules/favorite/services/favorite-controls.service';
import { UserService } from 'src/app/modules/user/services/user.service';
import { take } from 'rxjs/operators';
import { GraphicBehaviorType } from '@caslink/device-client/ng-app/shared/models/graphic-behavior-type.enum';
import {
    IControlDeviceModel,
    IDisplayInfo
} from '@caslink/device-client/ng-app/shared/models/models';
import DashboardDeviceSummary = CASLink.Device.Ui.ViewModel.Dashboard.DashboardDeviceSummary;

export type ArrayWithSetPoints = ExtendableWithSetPoints[];
export type ExtendableWithSetPoints = IDashboard | IDashboardAreaSummary | DashboardDeviceSummary | DashboardDeviceTile;

@Injectable({
    providedIn: 'root'
})
export class DashboardDeviceService {
    constructor(
        private favoritePlacesService: FavoritePlacesService,
        private favoriteSpacesService: FavoriteSpacesService,
        private favoriteControlsService: FavoriteControlsService,
        private dashboardSubscriptionService: DashboardSubscriptionService,
        private dashboardViewService: DashboardViewService,
        private userService: UserService,
        private factory: DeviceModelFactoryService,
        private router: Router
    ) {
    }

    private static setParentInsights(parent: DashboardParentTile, child: DashboardTile) {
        if (child.showInsights && !parent.showInsights) {
            parent.showInsights = true;
        }
    }

    private static setupDisplayInfoForDevice(device: DashboardDeviceTile) {
        device.displayInfo = {
            dataViewModel: device.controls && device.controls.displayInfo ?
                device.controls.displayInfo.dataViewModel as ICardDataView[] : [],
            isCardEditable: false
        } as IDisplayInfo;
    }

    private static setDeviceThumbnail(device: DashboardDeviceTile) {
        if (deviceTypeIcons.has(device.category)) {
            device.thumbnail = deviceTypeIcons.get(device.category);
        }
    }

    private static setParentBehavior(
        parent: DashboardParentTile,
        parentProperty: keyof DashboardParentTileEditableProperties & (
            'lights' | 'fans' | 'occupancy' | 'roomLights' | 'hoodLights' | 'roomFans' | 'hoodFans'
        ),
        on: boolean,
        hardwareItemId: number,
        hardwareItemName: string,
        sensorId: number,
        visible: boolean,
        enabled: boolean // dcv2 + light or fan
    ) {
        const parentSensorProperty = sensorFields.get(parentProperty);
        if (!parent[parentProperty]) {
            const toggleSensor: Partial<GraphicBehaviorToggleSensor> = {
                on,
                visible,
                enabled,
                onSensorCount: on ? 1 : 0,
                onSensorCountVisible: true,
                onSensorsByDevice: new Map<number, number>([ [ hardwareItemId, (on ? 1 : 0) ] ]),
                totalSensors: 1,
                totalSensorsByDevice: new Map<number, number>([ [ hardwareItemId, 1 ] ]),
            };

            if (parentProperty === 'lights') {
                parent[parentProperty] = {
                    sensor: {
                        graphicBehaviorType: GraphicBehaviorType.LIGHT,
                        ...toggleSensor
                    } as LightsToggleSensor,
                    sensorId: null,
                }
            } else if (parentProperty === 'roomLights') {
                parent[parentProperty] = {
                    sensor: {
                        graphicBehaviorType: GraphicBehaviorType.LIGHT_ROOM,
                        ...toggleSensor,
                        name: 'Lights'
                    } as RoomLightToggleSensor,
                    sensorId: null,
                }
            } else if (parentProperty === 'hoodLights') {
                parent[parentProperty] = {
                    sensor: {
                        graphicBehaviorType: GraphicBehaviorType.LIGHT_HOOD,
                        ...toggleSensor,
                        name: 'Hood Lights'
                    } as HoodLightToggleSensor,
                    sensorId: null,
                }
            } else if (parentProperty === 'fans') {
                parent[parentProperty] = {
                    sensor: {
                        graphicBehaviorType: GraphicBehaviorType.FAN,
                        ...toggleSensor
                    } as FansToggleSensor,
                    sensorId: null,
                }
            } else if (parentProperty === 'roomFans') {
                parent[parentProperty] = {
                    sensor: {
                        graphicBehaviorType: GraphicBehaviorType.FAN_ROOM,
                        ...toggleSensor,
                        name: 'Fans'
                    } as RoomFanToggleSensor,
                    sensorId: null,
                }
            } else if (parentProperty === 'hoodFans') {
                parent[parentProperty] = {
                    sensor: {
                        graphicBehaviorType: GraphicBehaviorType.FAN_HOOD,
                        ...toggleSensor,
                        name: 'Hood Fans'
                    } as HoodFanToggleSensor,
                    sensorId: null,
                }
            } else if (parentProperty === 'occupancy') {
                parent[parentProperty] = {
                    sensor: {
                        graphicBehaviorType: GraphicBehaviorType.OCCUPIED_SENSOR,
                        ...toggleSensor,
                        name: 'Occupied',
                        onSensorCountVisible: false
                    } as OccupancyToggleSensor,
                    sensorId: null,
                }
            }
        } else {
            if (on) {
                if (!parent[parentProperty].sensor.on) {
                    parent[parentProperty].sensor.on = true;
                }

                parent[parentProperty].sensor.onSensorCount++;
                const onSensorsForDevice = parent[parentProperty].sensor.onSensorsByDevice.get(hardwareItemId);
                const newOnSensorCount = (onSensorsForDevice ? onSensorsForDevice : 0) + 1;
                parent[parentProperty].sensor.onSensorsByDevice.set(hardwareItemId, newOnSensorCount);
            }

            parent[parentProperty].sensor.totalSensors++;
            const totalSensorsForDevice = parent[parentProperty].sensor.totalSensorsByDevice.get(hardwareItemId);
            const newSensorCount = (totalSensorsForDevice ? totalSensorsForDevice : 0) + 1;
            parent[parentProperty].sensor.totalSensorsByDevice.set(hardwareItemId, newSensorCount);
        }

        if (parent[parentSensorProperty] === undefined) {
            parent[parentSensorProperty] = {
                visible,
                enabled,
                sensorIds: new Map<number, Set<number>>([ [hardwareItemId, new Set([sensorId])] ]),
            };
        } else {
            if (!parent[parentSensorProperty].visible && visible) {
                parent[parentSensorProperty].visible = true;
            }

            if (!parent[parentSensorProperty].enabled && enabled) {
                parent[parentSensorProperty].enabled = true;
            }

            if (parent[parentSensorProperty].sensorIds.has(hardwareItemId)) {
                parent[parentSensorProperty].sensorIds.get(hardwareItemId).add(sensorId);
            } else {
                parent[parentSensorProperty].sensorIds.set(hardwareItemId, new Set([sensorId]));
            }
        }

        if (parent.hardwareItems) {
            parent.hardwareItems.set(hardwareItemId, hardwareItemName);
        }
    }

    private static setDeviceToggleBehavior(
        device: DashboardDeviceTile,
        graphicBehaviorType: GraphicBehaviorToggleSensor['graphicBehaviorType'],
        on: boolean,
        visible: boolean,
        enabled: boolean,
        sensorId: number,
        name: string,
    ) {
        let newSensor: GraphicBehaviorToggleSensor = { on, graphicBehaviorType, visible, enabled, onSensorCountVisible: true };
        if (name !== null) {
            newSensor.name = name;
        }
        if (device.graphicBehaviors === undefined) {
            device.graphicBehaviors = new Map<number, GraphicBehaviorToggleSensor>([[sensorId, newSensor]]);
        } else {
            const existingBehavior = device.graphicBehaviors.get(sensorId);
            if (existingBehavior) {
                existingBehavior.on = on;
                existingBehavior.visible = visible;
                existingBehavior.enabled = enabled;
                newSensor = existingBehavior;
            }
            device.graphicBehaviors.set(sensorId, newSensor);
        }
    }

    private static setDeviceOccupancy(
        device: DashboardDeviceTile,
        on: boolean,
        visible: boolean,
        enabled: boolean,
        sensorId: number,
        name: string,
    ) {
        if (device.occupancy === undefined) {
            device.occupancy = { on, graphicBehaviorType: GraphicBehaviorType.OCCUPIED_SENSOR, visible, enabled, onSensorCountVisible: false };
        } else {
            device.occupancy.on = on;
            device.occupancy.visible = visible;
            device.occupancy.enabled = enabled;
        }

        if (name !== null) {
            device.occupancy.name = name;
        }

        device.occupancySensorId = sensorId;
    }

    private static setDeviceEnumerationBehavior(
        device: DashboardDeviceTile,
        graphicBehaviorType: GraphicBehaviorEnumSensor['graphicBehaviorType'],
        value: number,
        visible: boolean,
        enabled: boolean,
        sensorId: number,
        options: SelectListOption[],
    ) {
        const newSensor: GraphicBehaviorEnumSensor = { value, graphicBehaviorType, visible, enabled, options };

        const sensorProp = graphicBehaviorType === GraphicBehaviorType.BLOWER ? 'occupiedBlower' : 'unoccupiedBlower';
        const sensorIdProp = graphicBehaviorType === GraphicBehaviorType.BLOWER ? 'occupiedBlowerSensorId' : 'unoccupiedBlowerSensorId';

        if (device[sensorProp] === undefined) {
            device[sensorProp] = newSensor;
        } else {
            const existingBehavior = device[sensorProp];
            if (existingBehavior) {
                existingBehavior.value = value;
                existingBehavior.visible = visible;
                existingBehavior.enabled = enabled;
            }
        }

        device[sensorIdProp] = sensorId;
    }

    private static handleGraphicBehavior(
        device: DashboardDeviceTile,
        area: IDashboardAreaSummary,
        dashboard: IDashboard,
        sensor: any,
        graphicBehaviorType: GraphicBehaviorToggleSensor['graphicBehaviorType'],
        setDeviceProp: (
            d: DashboardDeviceTile,
            graphicBehaviorType: GraphicBehaviorToggleSensor['graphicBehaviorType'],
            on: boolean,
            visible: boolean,
            enabled: boolean,
            sensorId: number,
            name: string
        ) => void,
        parentProperty: keyof DashboardParentTile & (
            'lights' | 'fans' | 'occupancy' | 'roomLights' | 'hoodLights' | 'roomFans' | 'hoodFans'
        ),
        setParentProp: (
            parent: DashboardParentTile,
            parentProperty: keyof DashboardParentTile & (
                'lights' | 'fans' | 'occupancy' | 'roomLights' | 'hoodLights' | 'roomFans' | 'hoodFans'
            ),
            on: boolean,
            hardwareItemId: number,
            deviceName: string,
            sensorId: number,
            visible: boolean,
            enabled: boolean
        ) => void
    ) {
        const name = DashboardDeviceService.getSensorName(sensor);
        const enabled = DashboardDeviceService.isSensorEnabled(device, sensor);
        setDeviceProp(device, graphicBehaviorType, sensor.actualValue, sensor.isVisible, enabled, sensor.sensorId, name);
        setParentProp(
            area,
            parentProperty,
            sensor.actualValue,
            device.hardwareItem.id,
            device.tag,
            sensor.sensorId,
            sensor.isVisible,
            enabled
        );
        setParentProp(
            dashboard,
            parentProperty,
            sensor.actualValue,
            device.hardwareItem.id,
            device.tag,
            sensor.sensorId,
            sensor.isVisible,
            enabled
        );
    }

    private static setParentTemperature(parent: DashboardParentTile, child: DashboardTile, childId: number) {
        if (parent.childTemperatures === undefined) {
            parent.childTemperatures = new Map<number, number>();
        }

        const childTemperature = Number(child.temperature.text);
        parent.childTemperatures.set(childId, childTemperature);

        let childTemperatureCount = 0;
        let childTemperatureSum = 0;
        parent.childTemperatures.forEach((value) => {
            childTemperatureCount++;
            childTemperatureSum += value;
        });

        parent.temperature = {
            text: (Math.round(childTemperatureSum / childTemperatureCount)).toString(),
            units: '°',
        };
    }

    private static setParentRelativeHumidity(parent: DashboardParentTile, child: DashboardTile, childId: number) {
        if (parent.childRelativeHumidity === undefined) {
            parent.childRelativeHumidity = new Map<number, number>();
        }

        const childRelativeHumidity = Number(child.relativeHumidity?.text);
        parent.childRelativeHumidity.set(childId, childRelativeHumidity);

        let childRelativeHumidityCount = 0;
        let childRelativeHumiditySum = 0;
        parent.childRelativeHumidity.forEach((value) => {
            childRelativeHumidityCount++;
            childRelativeHumiditySum += value;
        });

        parent.relativeHumidity = {
            text: Number(childRelativeHumiditySum / childRelativeHumidityCount).toFixed(1),
            units: '%',
        };
    }

    private setupSetpoints(device: DashboardDeviceTile, dataViewModel: any) {
        const setpointsData = this.getSetpointsFromViewModel(dataViewModel);
        device.occupiedSetpoint = this.prepairSetpointData(setpointsData, GraphicBehaviorType.TEMPERATURE_HOT_SENSOR, GraphicBehaviorType.TEMPERATURE_COLD_SENSOR);
        device.unoccupiedSetpoint = this.prepairSetpointData(setpointsData, GraphicBehaviorType.TEMPERATURE_HOT_UNOCCUPIED_SENSOR, GraphicBehaviorType.TEMPERATURE_COLD_UNOCCUPIED_SENSOR);
    }

    private prepairSetpointData(
        setpointsData: Map<GraphicBehaviorType, any>,
        heating: GraphicBehaviorType.TEMPERATURE_HOT_SENSOR | GraphicBehaviorType.TEMPERATURE_HOT_UNOCCUPIED_SENSOR,
        cooling: GraphicBehaviorType.TEMPERATURE_COLD_SENSOR | GraphicBehaviorType.TEMPERATURE_COLD_UNOCCUPIED_SENSOR,
    ) {
        const coolingData = setpointsData.get(cooling);
        const heatingData = setpointsData.get(heating);

        return {
            heating: this.isEmptyObject(heatingData) ? null : heatingData,
            cooling: this.isEmptyObject(coolingData) ? null : coolingData,
            maximum: DashboardDeviceService.getSetpointMaxium(coolingData?.maxValue, heatingData?.maxValue),
            minimum: DashboardDeviceService.getSetpointMinium(coolingData?.minValue, heatingData?.minValue),
        };
    }

    private getSetpointsFromViewModel(dataViewModel: any) {
        const setpointGraphicBehaviors = [
            GraphicBehaviorType.TEMPERATURE_COLD_SENSOR,
            GraphicBehaviorType.TEMPERATURE_COLD_UNOCCUPIED_SENSOR,
            GraphicBehaviorType.TEMPERATURE_HOT_SENSOR,
            GraphicBehaviorType.TEMPERATURE_HOT_UNOCCUPIED_SENSOR,
        ];

        const setpointsData = new Map<GraphicBehaviorType, any>();

        for (const setpointGraphicBehavior of setpointGraphicBehaviors) {
            const setpoint = dataViewModel.find(data => data.isGraphicBehavior && data.graphicBehavior === setpointGraphicBehavior);
            if (setpoint) {
                setpointsData.set(setpointGraphicBehavior, setpoint.data);
            }
        }
        return setpointsData;
    }

    private isEmptyObject(obj: any) {
        return !obj || Object.keys(obj).length === 0;
    }

    public static getSetpointMinium(heatingMinValue: number | undefined, coolingMinValue: number | undefined) {
        if (!isNaN(heatingMinValue) && !isNaN(coolingMinValue)) {
            return Math.min(heatingMinValue, coolingMinValue);
        }
        return !isNaN(heatingMinValue) ? heatingMinValue : coolingMinValue;
    }

    public static getSetpointMaxium(heatingMaxValue: number, coolingMaxValue: number) {
        if (!isNaN(heatingMaxValue) && !isNaN(coolingMaxValue)) {
            return Math.max(heatingMaxValue, coolingMaxValue);
        }
        return !isNaN(heatingMaxValue) ? heatingMaxValue : coolingMaxValue;
    }

    private static getSensorName(sensor: any) {
        return sensor.categories && sensor.categories.length > 0 ? sensor.categories[0] : sensor.name;
    }

    private static isSensorEnabled(device: DashboardDeviceTile, sensor: any) {
        return device.hasControlPermission && sensor.isEnabled;
    }

    private static checkRange(minRange: number, maxRange: number) {
        return !isNaN(minRange) &&
            !isNaN(maxRange) &&
            minRange + 1 < maxRange;
    }

    private static convertInfToUndefined(n: number) {
        return n === Infinity || n === -Infinity ? undefined : n;
    }

    private static childHasControlPermission(arr: ArrayWithSetPoints) {
        if (arr && arr.length) {
            return !!arr.filter(a => a.hasControlPermission).length;
        }
        return false;
    }

    private static deviceHasScheduling(device: DashboardDeviceTile | DashboardDeviceSummary) {
        return device &&
            device.controls &&
            device.controls.commands &&
            device.controls.commands.scheduling &&
            device.controls.commands.scheduling.isVisible;
    }

    public static getDevicesWithScheduling(arr: IDashboardAreaSummary[]) {
        const devicesWithScheduling: DashboardDeviceSummary[] = [];
        arr.forEach(area => {
            if (area && area.devices) {
                area.devices.forEach(item => {
                    if (DashboardDeviceService.deviceHasScheduling(item)) {
                        devicesWithScheduling.push(item);
                    }
                });
            }
        });
        return devicesWithScheduling;
    }

    public map(dashboard: any): IDashboardDeviceSummary[] {
        const devices = [];

        for (const area of dashboard.areas) {
            for (const device  of area.devices) {
                const dev = device as unknown as IDashboardDeviceSummary;
                dev.areaTag = area.areaTag;
                dev.areaId = area.areaId;
                dev.areaIcon = area.areaIcon;

                devices.push(dev);
            }
        }

        return devices;
    }

    public mapControls(device: IDashboardDeviceSummary) {
        const controlDevice: IControlDeviceModel & any = {
            controls: [device.controls],
            descriptor: ''
        };

        device.model = this.factory.createDynamic(
            device.hardwareCategory,
            controlDevice,
            device.hardwareItem.areaId,
            device.locationId,
            true
        );

        if (device.controls) {
            device.controls.startLiveModelUpdates =
                () => this.dashboardSubscriptionService.setupLiveViewModels(device.locationId, device.areaId, device.controls.controlSummaryViewModel);
            device.controls.stopLiveModelUpdates =
                () => this.dashboardSubscriptionService.stopLiveViewModels(device.locationId, device.areaId, device.controls.controlSummaryViewModel);
        }
    }

    public setupControlsForDevice(dashboard: IDashboard, device: DashboardDeviceTile, area: IDashboardAreaSummary) {
        device.areaId = area.areaId;

        device.showInsights = device.resource !== undefined;
        DashboardDeviceService.setParentInsights(dashboard, device);

        device.deviceStatusType = device.controls.controlSummaryViewModel.deviceStatusType;

        this.mapControls(device);

        this.addDeviceViewModelUpdatesHandlersToDashboard(dashboard, device);

        // remove behaviors before setting the new ones up to prevent cached behaviors from persisting across signalr updates
        if (device.graphicBehaviors) {
            device.graphicBehaviors = undefined;
        }

        this.setGraphicBehavior(dashboard, device, area);
        DashboardDeviceService.setupDisplayInfoForDevice(device);
        this.setupDeviceSupplementalButtons(device, area.areaId);
        DashboardDeviceService.setDeviceThumbnail(device);

        if (dashboard && dashboard.areas) {
            dashboard.areas.forEach(a => {
                this.extendDashboardBySetpoints(a, a.devices);
            });
        }
        this.extendDashboardBySetpoints(dashboard, dashboard.areas);
        this.extendDashboardByButtons(dashboard, dashboard.areas);
    }

    public setupControlsForEditMode(deviceTile: DashboardDeviceTile) {
        deviceTile.deviceStatusType = deviceTile.deviceStatusType;
        this.mapControls(deviceTile);

        const dashboard: IDashboard = {} as IDashboard;
        this.setGraphicBehavior(dashboard, deviceTile, {areaId: null, hardwareItems: new Map<number, string>() } as any);

        return deviceTile;
    }

    public extendDashboardBySetpoints(d: ExtendableWithSetPoints, arr: ArrayWithSetPoints): ExtendableWithSetPoints {
        this.extendDashboard(d, arr);
        return d;
    }

    public extendDashboardByButtons(d: IDashboard, arr: IDashboardAreaSummary[]) {
        if (arr && arr.length) {
            DashboardDeviceService.getDevicesWithScheduling(arr);
            d.buttons = [];
            this.pushButtonIfExists(d.buttons, this.createPlaceInsightsButton(d));
            this.pushButtonIfExists(d.buttons, this.createOccupancyScheduleButton(d));
        }
    }

    public extendDashboard(dashboard: ExtendableWithSetPoints, arr: ArrayWithSetPoints) {
        const [occupiedSetpointMinimum, occupiedSetpointMaximum] = this.getLargestRangeFromArray(arr, ['occupiedSetpoint', 'minimum'], ['occupiedSetpoint', 'maximum']);
        const [unoccupiedSetpointMinimum, unoccupiedSetpointMaximum] = this.getLargestRangeFromArray(arr, ['unoccupiedSetpoint', 'minimum'], ['unoccupiedSetpoint', 'maximum']);

        if (DashboardDeviceService.checkRange(occupiedSetpointMinimum, occupiedSetpointMaximum)) {
            const [occupiedHeatingSetpoint, occupiedCoolingSetpoint] = this.getSmallestRangeFromArray(arr, ['occupiedSetpoint', 'heating', 'value'], ['occupiedSetpoint', 'cooling', 'value']);
            dashboard['occupiedSetpoint'] = this.createExtendedSetpoint(occupiedCoolingSetpoint, occupiedHeatingSetpoint, occupiedSetpointMinimum, occupiedSetpointMaximum);
            dashboard.hasControlPermission = DashboardDeviceService.childHasControlPermission(arr);
        }

        if (DashboardDeviceService.checkRange(unoccupiedSetpointMinimum, unoccupiedSetpointMaximum)) {
            const [unoccupiedHeatingSetpoint, unoccupiedCoolingSetpoint] = this.getSmallestRangeFromArray(arr, ['unoccupiedSetpoint', 'heating', 'value'], ['unoccupiedSetpoint', 'cooling', 'value']);
            dashboard['unoccupiedSetpoint'] = this.createExtendedSetpoint(unoccupiedCoolingSetpoint, unoccupiedHeatingSetpoint, unoccupiedSetpointMinimum, unoccupiedSetpointMaximum);
            dashboard.hasControlPermission = DashboardDeviceService.childHasControlPermission(arr);
        }
    }

    private createExtendedSetpoint(cooling: number, heating: number, minimum: number, maximum: number) {
        return {
            cooling: {
                value: DashboardDeviceService.convertInfToUndefined(cooling),
            },
            heating: {
                value: DashboardDeviceService.convertInfToUndefined(heating),
            },
            maximum: DashboardDeviceService.convertInfToUndefined(maximum),
            minimum: DashboardDeviceService.convertInfToUndefined(minimum),
        };
    }

    private getValueOfNestedObject(nestedObject: ExtendableWithSetPoints, pathArray: string[]) {
        return pathArray.reduce((current, key) => (current && current[key] !== undefined) ? current[key] : undefined, nestedObject);
    }

    private getMinimumFromArray(arr: ArrayWithSetPoints, pathArray: string[]) {
        return Math.min(...arr.map(o => this.getValueOfNestedObject(o, pathArray) ?? Infinity), Infinity);
    }

    private getMaximumFromArray(arr: ArrayWithSetPoints, pathArray: string[]) {
        return Math.max(...arr.map(o => this.getValueOfNestedObject(o, pathArray) ?? -Infinity), -Infinity);
    }

    private getSmallestRangeFromArray(arr: ArrayWithSetPoints, minimumPathArray: string[], maximumPathArray: string[]) {
        return [this.getMaximumFromArray(arr, minimumPathArray), this.getMinimumFromArray(arr, maximumPathArray)];
    }

    private getLargestRangeFromArray(arr: ArrayWithSetPoints, minimumPathArray: string[], maximumPathArray: string[]) {
        return [this.getMinimumFromArray(arr, minimumPathArray), this.getMaximumFromArray(arr, maximumPathArray)];
    }

    private addDeviceViewModelUpdatesHandlersToDashboard(dashboard: IDashboard, device: DashboardDeviceTile) {
        if (device.controls.startLiveModelUpdates) {
            dashboard.startViewModelUpdates.set(device.hardwareItem.id, new Observable(obs => {
                if (!device.subscribedToUpdates) {
                    device.controls.startLiveModelUpdates();
                    device.subscribedToUpdates = true;
                    obs.next();
                }
                obs.complete();
            }));

            dashboard.stopViewModelUpdates.set(device.hardwareItem.id, new Observable(obs => {
                if (device.subscribedToUpdates) {
                    device.controls.stopLiveModelUpdates();
                    device.subscribedToUpdates = false;
                    obs.next();
                }
                obs.complete();
            }));

            const placeFavorited = this.favoritePlacesService.isFavorite(dashboard.locationId);
            const spaceFavorited = this.favoriteSpacesService.isFavorite({
                    locationId: dashboard.locationId,
                    spaceId: device.areaId
                });
            const controlFavorited = this.favoriteControlsService.isFavorite({
                    locationId: dashboard.locationId,
                    spaceId: device.areaId,
                    controlId: device.hardwareItem.id as number
                });

            const subscribeToDevice = () => {
                device.subscribedToUpdates = true;
                device.controls.startLiveModelUpdates();
            };

            if (!device.subscribedToUpdates) {
                if (placeFavorited || spaceFavorited || controlFavorited) {
                    subscribeToDevice();
                } else {
                    this.userService.getLastActiveLocation().pipe(take(1)).subscribe(activeLocation => {
                        if (activeLocation?.id === dashboard.locationId) {
                            subscribeToDevice();
                        }
                    });
                }
            }
        }
    }

    private setGraphicBehavior(dashboard: IDashboard, device: DashboardDeviceTile, area: IDashboardAreaSummary) {
        let numAlerts = 0;
        let hasTemperatureGraphic = false;
        let hasRelativeHumidityGraphic = false;

        this.setupSetpoints(device, device.controls.displayInfo.dataViewModel);

        device.controls.displayInfo.dataViewModel.forEach(d => {
            function handleGraphicBehavior(
                deviceProp: GraphicBehaviorToggleSensor['graphicBehaviorType'],
                parentProp: keyof DashboardParentTile & (
                    'lights' | 'fans' | 'occupancy' | 'roomLights' | 'hoodLights' | 'roomFans' | 'hoodFans'
                )
            ) {
                DashboardDeviceService.handleGraphicBehavior(
                    device,
                    area,
                    dashboard,
                    d,
                    deviceProp,
                    DashboardDeviceService.setDeviceToggleBehavior,
                    parentProp,
                    DashboardDeviceService.setParentBehavior);
            }

            if (d.isGraphicBehavior) {
                if (d.graphicBehavior === GraphicBehaviorType.ALERT) {
                    numAlerts++;
                } else if (d.graphicBehavior === GraphicBehaviorType.CLIMATE_CONTROL_STATE_SENSOR) {
                    if (d.displayValue === 'Heating') {
                        device.isHeating = true;
                        area.isHeating = true;
                        dashboard.isHeating = true;
                    } else if (d.displayValue === 'Cooling') {
                        device.isCooling = true;
                        area.isCooling = true;
                        dashboard.isCooling = true;
                    } else if (d.displayValue === 'Heating Cooling') {
                        device.isHeating = true;
                        area.isHeating = true;
                        dashboard.isHeating = true;

                        device.isCooling = true;
                        area.isCooling = true;
                        dashboard.isCooling = true;
                    }
                } else if (!hasTemperatureGraphic && d.graphicBehavior === GraphicBehaviorType.TEMPERATURE_SENSOR) {
                    hasTemperatureGraphic = true;

                    const temperature = Math.round(d.actualValue);
                    device.temperature = { text: temperature.toString(), units: '°' };
                    DashboardDeviceService.setParentTemperature(area, device, device.hardwareItem.id);
                    DashboardDeviceService.setParentTemperature(dashboard, device, device.hardwareItem.id);
                } else if (d.graphicBehavior === GraphicBehaviorType.LIGHT) {
                    handleGraphicBehavior(d.graphicBehavior, 'lights');
                } else if (d.graphicBehavior === GraphicBehaviorType.LIGHT_ROOM) {
                    handleGraphicBehavior(d.graphicBehavior, 'roomLights');
                } else if (d.graphicBehavior === GraphicBehaviorType.LIGHT_HOOD) {
                    handleGraphicBehavior(d.graphicBehavior, 'hoodLights');
                } else if (d.graphicBehavior === GraphicBehaviorType.FAN) {
                    handleGraphicBehavior(d.graphicBehavior, 'fans');
                } else if (d.graphicBehavior === GraphicBehaviorType.FAN_ROOM) {
                    handleGraphicBehavior(d.graphicBehavior, 'roomFans');
                } else if (d.graphicBehavior === GraphicBehaviorType.FAN_HOOD) {
                    handleGraphicBehavior(d.graphicBehavior, 'hoodFans');
                } else if (d.graphicBehavior === GraphicBehaviorType.BLOWER || d.graphicBehavior === GraphicBehaviorType.BLOWER) {
                    // Don't propagate blower fans up to areas or locations.
                    const enabled = DashboardDeviceService.isSensorEnabled(device, d);
                    const options = device.controls.controlSummaryViewModel.selectLists[d.data.fieldRelationship.relatedFieldId].items;
                    DashboardDeviceService.setDeviceEnumerationBehavior(
                        device,
                        d.graphicBehavior,
                        d.actualValue,
                        d.isVisible,
                        enabled,
                        d.sensorId,
                        options);
                } else if (d.graphicBehavior === GraphicBehaviorType.OCCUPIED_SENSOR) {
                    DashboardDeviceService.handleGraphicBehavior(
                        device,
                        area,
                        dashboard,
                        d,
                        GraphicBehaviorType.OCCUPIED_SENSOR,
                        (
                            tile: DashboardDeviceTile,
                            _: GraphicBehaviorToggleSensor['graphicBehaviorType'],
                            on: boolean,
                            visible: boolean,
                            enabled: boolean,
                            sensorId: number,
                            name: string
                        ) => DashboardDeviceService.setDeviceOccupancy(tile, on, visible, enabled, sensorId, name),
                        'occupancy',
                        DashboardDeviceService.setParentBehavior);
                } else if (
                    !hasRelativeHumidityGraphic && d.graphicBehavior === GraphicBehaviorType.RELATIVE_HUMIDITY_SENSOR
                ) {
                    hasRelativeHumidityGraphic = true;
                    const relativeHumidity = Math.round(d.actualValue);
                    device.relativeHumidity = { text: relativeHumidity.toString(), units: '°' };
                    DashboardDeviceService.setParentRelativeHumidity(area, device, device.hardwareItem.id);
                    DashboardDeviceService.setParentRelativeHumidity(dashboard, device, device.hardwareItem.id);
                }
            }
        });

        if (dashboard.deviceAlerts) {
            const priorAlerts = dashboard.deviceAlerts.get(device.hardwareItem.id) || 0;
            dashboard.deviceAlerts.set(device.hardwareItem.id, numAlerts);
            let areaAlerts = dashboard.areaAlerts.get(area.areaId || -1) || 0;
            // Remove the alerts for the existing device so they get properly displaced by the up to date count.
            if (priorAlerts > 0 && areaAlerts - priorAlerts >= 0) {
                areaAlerts -= priorAlerts;
            }
            dashboard.areaAlerts.set(area.areaId || -1, areaAlerts + numAlerts);
            if (priorAlerts > 0 && dashboard.alertCount - priorAlerts >= 0) {
                dashboard.alertCount -= priorAlerts;
            }
            dashboard.alertCount += numAlerts;
        }
    }

    private pushButtonIfExists(buttons: ActionButton[], button: ActionButton){
        if(button == null){
            return;
        }
        buttons.push(button);
    }

    private setupDeviceSupplementalButtons(device: DashboardDeviceTile, areaId: number) {
        device.buttons = [];
        this.pushButtonIfExists(device.buttons, this.createInsightsButton(device, areaId));
        this.pushButtonIfExists(device.buttons, this.createScheduleButton(device, areaId));
        this.pushButtonIfExists(device.buttons, this.createSettingsButton(device, areaId));
        this.pushButtonIfExists(device.buttons, this.createAdvancedButton(device, areaId));
        this.pushButtonIfExists(device.buttons, this.createFactoryButton(device, areaId));
    }

    private createSettingsButton = (device: DashboardDeviceTile, areaId: number): ActionButton => {
        const isVisible = device && device.hasControlPermission;
        if(!isVisible){
            return null;
        }
        return {
            title: () => 'Settings',
            icon: () => 'settings',
            isHidden: () => !isVisible,
            isDisabled: () => false,
            method: () => this.showSettings(device, areaId),
        };
    }

    private createAdvancedButton = (device: DashboardDeviceTile, areaId: number): ActionButton => {
        const isVisible = device &&
            device.controls &&
            device.controls.commands &&
            device.controls.commands.currentInformation &&
            device.controls.commands.currentInformation.isVisible;
            if(!isVisible){
                return null;
            }
        return {
            title: () => 'Advanced Settings',
            icon: () => 'settings_suggest',
            method: () => this.showAdvanced(device, areaId),
            isDisabled: () => false,
            isHidden: () => !isVisible || !this.dashboardViewService.showControlsLink(device)
        };
    }

    private createScheduleButton = (device: DashboardDeviceTile, areaId: number) => {
        const isVisible = DashboardDeviceService.deviceHasScheduling(device);
        if(!isVisible){
            return null;
        }
        return {
            title: () => 'Schedule',
            icon: () => 'event_note',
            method: () => this.showIntegrateControlScheduling(device, areaId),
            isDisabled: () => false,
            isHidden: () => !isVisible,
        };
    }

    private createInsightsButton =(device: DashboardDeviceTile, areaId: number) => {
        const isVisible = device.showInsights;
        if(!isVisible){
            return null;
        }
        return {
            title: () => 'Insights',
            icon: () => 'insights',
            method: () => this.showDeviceInsights(device, areaId),
            isDisabled: () => false,
            isHidden: () => !isVisible,
        };
    }

    private createPlaceInsightsButton = (dashboard: IDashboard) => {
        const isVisible = dashboard.showInsights;
        if(!isVisible){
            return null;
        }
        return {
            title: () => 'Insights',
            icon: () => 'insights',
            method: () => {
                this.router.navigate([`places/${dashboard.locationId}/insights`]);
            },
            isDisabled: () => false,
            isHidden: () => !isVisible,
        };
    }

    private createOccupancyScheduleButton = (dashboard: IDashboard) => {
        return {
            title: () => 'Schedule',
            icon: () => 'event_note',
            method: () => {
                this.router.navigateByUrl(`/location/${dashboard.locationId}/occupancy`);
            },
            isDisabled: () => false,
            isHidden: () => false,
        };
    }

    private createFactoryButton = (device: DashboardDeviceTile, areaId: number): ActionButton => {
        const isVisible = device &&
            device.controls &&
            device.controls.commands &&
            device.controls.commands.systemInformation &&
            device.controls.commands.systemInformation.isVisible;
        if(!isVisible){
            return null;
        }
        return {
            title: () => 'Factory Settings',
            icon: () => 'settings_applications',
            isHidden: () => !isVisible,
            isDisabled: () => false,
            method: () => this.showFactory(device, areaId)
        };
    }

    private showDeviceInsights = ({ locationId: loc, hardwareItem: { id: hwId } }: DashboardDeviceTile, areaId: number) => {
        this.goToUrl(`places/${loc}/spaces/${areaId}/controls/${hwId}/insights`);
    }

    private showIntegrateControlScheduling = ({ locationId: loc, hardwareItem: { id: hwId } }: DashboardDeviceTile, areaId: number) => {
        this.goToUrl(`places/${loc}/spaces/${areaId}/controls/${hwId}/settings/schedule`);
    }

    private showSettings = ({ locationId: loc, hardwareItem: { id: hwId } }: DashboardDeviceTile, areaId: number) => {
        this.goToUrl(`places/${loc}/spaces/${areaId}/controls/${hwId}/settings/basic`);
    }

    private showAdvanced = ({ locationId: loc, hardwareItem: { id: hwId } }: DashboardDeviceTile, areaId: number) => {
        this.goToUrl(`places/${loc}/spaces/${areaId}/controls/${hwId}/settings/advanced`);
    }

    private showFactory = ({ locationId: loc, hardwareItem: { id: hwId } }: DashboardDeviceTile, areaId: number) => {
        this.goToUrl(`places/${loc}/spaces/${areaId}/controls/${hwId}/settings/factory`);
    }

    private goToUrl = (url: string) => {
        this.router.navigate([url]);
    }
}
